import { RelatedTermTypes } from '../shared/components/styled/search/types';
import { Entity, EntityType, TermRelationshipType } from '../../../types.generated';
import EntityRegistry from '../EntityRegistry';
import { useListCustomRelationTypesQuery } from '../../../graphql/glossary.generated';

export const ASSOCIATED_WITH = 'associatedWithGlossary';
export const UTILIZED_BY = 'utilizedBy';
export const GLOSSARY_TERM = 'glossaryTerm';
export const GLOSSARY_NODE = 'glossaryNode';
export const INVERSE_SUFFIX = ':inverse';

// Mapping from GraphQL relationship type to RelatedTermTypes key
export const RELATIONSHIP_TYPE_MAP: Record<string, string> = {
    IsA: 'Inherits',
    HasA: 'Contains',
    UtilizedBy: 'Utilized by',
    AssociatedWithGlossary: 'Associated with',
    IsTypeOf: 'Type Of',
    HasType: 'Has Type',
    HasAssociation: 'Has Association',
    UtilizedIn: 'Utilized In',
    ContainedBy: 'Contained by',
    InheritedBy: 'Inherited by',
    AssociatedWith: 'Associated with',
};

export function sortGlossaryTerms(entityRegistry: EntityRegistry, nodeA?: Entity | null, nodeB?: Entity | null) {
    const nodeAName = entityRegistry.getDisplayName(EntityType.GlossaryTerm, nodeA) || '';
    const nodeBName = entityRegistry.getDisplayName(EntityType.GlossaryTerm, nodeB) || '';
    return nodeAName.localeCompare(nodeBName);
}

export function getRelatedEntitiesUrl(entityRegistry: EntityRegistry, urn: string) {
    return `${entityRegistry.getEntityUrl(EntityType.GlossaryTerm, urn)}/${encodeURIComponent('Related Entities')}`;
}

export function isInverseRelationship(relationshipType: RelatedTermTypes) {
    const inverseRelationships = [
        RelatedTermTypes.isTypeOf,
        RelatedTermTypes.isAChildren,
        RelatedTermTypes.hasAssociationTerms,
        RelatedTermTypes.utilizedInTerms,
        RelatedTermTypes.containedBy,
    ];

    return inverseRelationships.includes(relationshipType);
}

export function getRelationshipType(relationshipType = '') {
    const relationshipMapping = {
        // Direct relationships
        [RelatedTermTypes.typeOfRelations]: RelatedTermTypes.typeOfRelations,
        [RelatedTermTypes.associatedWithTerms]: ASSOCIATED_WITH,
        [RelatedTermTypes.utilizedByTerms]: UTILIZED_BY,
        [RelatedTermTypes.hasRelatedTerms]: TermRelationshipType.HasA,
        [RelatedTermTypes.isRelatedTerms]: TermRelationshipType.IsA,
        // Inverse relationships
        [RelatedTermTypes.isTypeOf]: RelatedTermTypes.typeOfRelations,
        [RelatedTermTypes.isAChildren]: TermRelationshipType.IsA,
        [RelatedTermTypes.hasAssociationTerms]: ASSOCIATED_WITH,
        [RelatedTermTypes.utilizedInTerms]: UTILIZED_BY,
        [RelatedTermTypes.containedBy]: TermRelationshipType.HasA,
    };

    return relationshipMapping[relationshipType] ?? relationshipType;
}

/**
 * Check if the relationship is a custom relation
 * @param relationship - The relationship to check
 * @returns True if the relationship is a custom relation, false otherwise
 */
export function isCustomRelation(relationship: string): boolean {
    return typeof relationship === 'string' && relationship.startsWith('urn:');
}

/**
 * Parse a selected key to extract the base URN and whether it's an inverse selection
 */
export function parseSelectedKey(selectedKey: string): { baseUrn: string; isInverseSelection: boolean } {
    const isInverseSelection = selectedKey.endsWith(INVERSE_SUFFIX);
    const baseUrn = isInverseSelection ? selectedKey.replace(INVERSE_SUFFIX, '') : selectedKey;
    return { baseUrn, isInverseSelection };
}

/**
 * Custom hook to process custom relation types for use in relationship dropdowns
 * @param entityType - The entity type to filter relations for (GLOSSARY_TERM or GLOSSARY_NODE)
 */
export function useCustomRelations(entityType: typeof GLOSSARY_TERM | typeof GLOSSARY_NODE) {
    const { data: customRelationTypesData } = useListCustomRelationTypesQuery();

    const customRelations = customRelationTypesData?.listCustomRelationTypes?.customRelations || [];
    const customRelationsMap: Record<string, { name: string; inverseName: string }> = {};
    const customRelationOptions: Array<{ urn: string; name: string; isInverse: boolean }> = [];
    const addedUrns = new Set<string>();

    customRelations.forEach((rel) => {
        // Build lookup map for all custom relations
        customRelationsMap[rel.urn] = { name: rel.name || '', inverseName: rel.inverseName || '' };

        // Add direct relation option (source matches entity type)
        if (rel.sourceEntityType === entityType && !addedUrns.has(rel.urn)) {
            customRelationOptions.push({
                urn: rel.urn,
                name: rel.name || rel.urn,
                isInverse: false,
            });
            addedUrns.add(rel.urn);
        }

        // Add inverse relation option (target matches entity type, show inverseName)
        const inverseUrn = `${rel.urn}${INVERSE_SUFFIX}`;
        if (rel.targetEntityType === entityType && rel.inverseName && !addedUrns.has(inverseUrn)) {
            customRelationOptions.push({
                urn: inverseUrn,
                name: rel.inverseName,
                isInverse: true,
            });
            addedUrns.add(inverseUrn);
        }
    });

    return { customRelationsMap, customRelationOptions };
}
